import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BlockSearchResultsAllComponent, CardType } from '@gallaghercp/core-frontend';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'core-block-search-results',
    templateUrl: './block-search-results-all.component.html',
    styleUrls: ['./block-search-results-all.component.scss'],
    standalone: false,
})
export class BlockClientSearchResultsAllComponent extends BlockSearchResultsAllComponent implements OnInit, OnDestroy {
    private readonly document = inject(DOCUMENT);

    currentZone = '';
    pages$!: Observable<any>;
    allPages: any[] = [];

    get isListFormat(): boolean {
        return (this.configService.get('search') as any)?.searchResultsInListFormat ?? false;
    }

    get searchPagination(): boolean {
        return (this.configService.get('search') as any)?.pagination ?? false;
    }

    get showResultIcon(): boolean {
        return (this.configService.get('search') as any)?.showResultIcon ?? false;
    }

    get showResultUrl(): boolean {
        return (this.configService.get('search') as any)?.showResultUrl ?? false;
    }

    get resultTitleColor(): boolean {
        return (this.configService.get('search') as any)?.resultTitleColor ?? 'var(--color-offset-black)';
    }

    override ngOnInit() {
        this.offset = 0;
        this.search.offset = 0;
        super.ngOnInit();
        this.limit = this.search.limit ?? 10;

        const currentZoneNumber = Number.parseInt(this.api.getServiceZone());
        // Use app.config zoneRouting mapping to resolve current zone route by id
        const zoneRouting = (this.configService.get('zoneRouting') ?? []) as any[];
        const matched = zoneRouting.find((z: any) => z && z.id === currentZoneNumber);
        if (matched && matched.route) {
            this.currentZone = String(matched.route);
        }

        this.subscriptions.push(
            this.pages!.getAll().subscribe((pages: any) => {
                this.pages$ = of(pages);
                this.allPages = pages;
            }),
        );

        this.subscriptions.push(
            this.search.results$
                .pipe(
                    switchMap((resultsObservable: any) => {
                        return resultsObservable.pipe(
                            tap((results: any) => {
                                if (this.searchPagination) {
                                    this.results = [...results];
                                    this.offset = this.search.offset;
                                    this.isLoading = false;
                                }
                            }),
                        );
                    }),
                )
                .subscribe(),
        );

        const currentQuery = this.search.query$.getValue();
        if (currentQuery && currentQuery.trim().length > 0) {
            this.isLoading = true;
            this.search.performSearch(currentQuery, false);
        }
    }

    override ngOnDestroy() {
        this.offset = 0;
        this.search.offset = 0;
        super.ngOnDestroy();
    }

    get paginatedResults(): any[] {
        return this.results || [];
    }

    // Get URL of parent
    parentUrl(card: CardType) {
        const foundParent = this.allPages.find((page: { id: number | undefined }) => page.id === card.parentId);
        return foundParent ? foundParent.url : this.currentZone;
    }

    get paginationItems(): any[] {
        const total = Math.ceil((this.total ?? 0) / this.limit);
        const current = Math.floor(this.offset / this.limit) + 1;
        const items: any[] = [];

        const addPage = (page: number) => {
            items.push({
                label: page,
                offset: (page - 1) * this.limit,
                active: current === page,
                isEllipsis: false,
            });
        };

        const addEllipsis = () => {
            items.push({
                label: '...',
                isEllipsis: true,
            });
        };

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                addPage(i);
            }
        } else {
            if (current <= 4) {
                for (let i = 1; i <= 5; i++) {
                    addPage(i);
                }
                addEllipsis();
                addPage(total);
            } else if (current >= total - 3) {
                addPage(1);
                addEllipsis();
                for (let i = total - 4; i <= total; i++) {
                    addPage(i);
                }
            } else {
                addPage(1);
                addEllipsis();
                addPage(current - 1);
                addPage(current);
                addPage(current + 1);
                addEllipsis();
                addPage(total);
            }
        }
        return items;
    }

    get hasPreviousPage(): boolean {
        return this.offset > 0;
    }

    get hasNextPage(): boolean {
        return this.offset + this.limit < (this.total ?? 0);
    }

    override previousPage() {
        if (this.hasPreviousPage && !this.isLoading) {
            this.navigateOffset(this.offset - this.limit);
        }
    }

    override nextPage() {
        if (this.hasNextPage && !this.isLoading) {
            this.navigateOffset(this.offset + this.limit);
        }
    }

    override navigateOffset(offset: number) {
        if (this.offset === offset && !this.isLoading) {
            return;
        }
        this.offset = offset;
        this.search.offset = offset;
        this.isLoading = true;
        this.search.performSearch(this.search.query$.getValue(), false);
    }

    override onScroll() {
        if (!this.searchPagination) {
            super.onScroll();
        }
    }

    override loadMore() {
        if (!this.searchPagination) {
            super.loadMore();
        }
    }

    override hasMoreContent(): boolean {
        if (this.searchPagination) {
            return false;
        }
        return this.offset + this.limit < this.total;
    }

    getFullResultLink(card: CardType): string {
        return `${this.document.location.host}${this.parentUrl(card)}/${card.url}`;
    }
}
